'use client';

import '@/features/cards/tokens';
import '@/features/gate/tokens';

import {
  useCreatorByHandler,
  useCreatorProfile,
  useCreatorTabs,
  useCreatorVideos,
} from '@/api/queries/creators.queries';
import { usePublicVideos } from '@/api/queries/freemium.queries';
import { Button } from '@/components/ui/button';
import { CreatorProfileSkeleton } from '@/features/creator/components/CreatorProfileSkeleton';
import type { PeekVideoRef } from '@/features/freemium/hooks/use-guest-gate';
import { AnalyticsEvent } from '@/shared/lib/analytics/events';
import { useAuthStore } from '@/shared/stores/auth-store';
import { useHeaderCtaStore } from '@/shared/stores/header-cta-store';
import { mergeCreatorTabs, SYSTEM_TAB_LABELS } from '@/shared/utils/creator-tabs-merge';
import { getCreatorRoute } from '@/shared/utils/routes';
import { buildSocialUrl } from '@/shared/utils/social';
import posthog from 'posthog-js';
import { useEffect, useMemo } from 'react';
import type { ConsumerTab } from '@/types/creator-tabs';
import { useCreatorGuestSummary } from './useCreatorGuestSummary';
import { CreatorHeader } from './CreatorHeader';
import { CreatorTabs } from './CreatorTabs';
import {
  FacebookIcon,
  InstagramIcon,
  ShortsIcon,
  TikTokIcon,
  VideoIcon,
  XIcon,
  YouTubeIcon,
} from '@/components/ui/social-icons';
import type { TabConfig } from './types';

interface CreatorLayoutClientProps {
  handler: string;
  children: React.ReactNode;
}

function manualRowToTab(row: ConsumerTab, base: string): TabConfig {
  return {
    key: row.uuid,
    label: row.title,
    isCustom: true,
    href: `${base}/t/${row.uuid}`,
    segments: ['t', row.uuid],
    ...(row.videos_count > 0 ? { count: row.videos_count } : {}),
    ...(row.icon ? { icon: row.icon } : {}),
    ...(row.color ? { color: row.color } : {}),
  };
}

/**
 * Default section visibility: guests keep unknown counts visible-locked until
 * the public count ships (self-heal), subscribers hide unless count > 0.
 */
function systemRowVisible(count: number | undefined, isGuest: boolean): boolean {
  if (isGuest) return count === undefined || count > 0;
  return count !== undefined && count > 0;
}

export function CreatorLayoutClient({ handler, children }: CreatorLayoutClientProps) {
  const user = useAuthStore((s) => s.user);
  const isSubscribed = useAuthStore((s) => s.isSubscribed);
  const isGuest = !user || !isSubscribed;
  const userId = user?.id;

  const registerUnlockCta = useHeaderCtaStore((s) => s.registerUnlockCta);
  const unregisterUnlockCta = useHeaderCtaStore((s) => s.unregisterUnlockCta);
  useEffect(() => {
    registerUnlockCta(handler);
    return () => unregisterUnlockCta(handler);
  }, [handler, registerUnlockCta, unregisterUnlockCta]);

  const {
    data: creatorData,
    isLoading: creatorLoading,
    error: creatorError,
    refetch: refetchCreator,
  } = useCreatorByHandler(handler, {
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const basicCreator = creatorData?.creators?.[0];
  const creatorId = useMemo(() => basicCreator?.id, [basicCreator?.id]);

  const { data: profileData } = useCreatorProfile(isGuest ? undefined : creatorId);

  const creator = useMemo(() => {
    if (!basicCreator) return profileData ?? basicCreator;
    if (!profileData) return basicCreator;
    // Merge: basicCreator has social fields the profile endpoint may lack
    return { ...basicCreator, ...profileData };
  }, [basicCreator, profileData]);

  const isOwner = !isGuest && Boolean(userId && creator?.user_id && userId === creator.user_id);

  // Profile tabs are public (guest-accessible, optional auth enrichment).
  // Unpublished/unknown channels get a backend 404 the query treats as empty.
  const { data: customTabs } = useCreatorTabs(creator?.id, {
    enabled: !!creator?.id,
  });

  // Fetch first video thumbnail as banner fallback (only when creator has no banner)
  const needsBannerFallback = !!creator && !creator.banner;
  const { data: authVideosForBanner } = useCreatorVideos(
    !isGuest && needsBannerFallback ? creator?.id : undefined,
    { sort_by: 'newest' }
  );
  const { data: publicVideosForBanner } = usePublicVideos(
    isGuest && needsBannerFallback && creator?.id
      ? { creators: creator.id, per_page: 1 }
      : undefined
  );
  const fallbackBanner = useMemo(() => {
    if (!needsBannerFallback) return null;
    const firstVideo = isGuest
      ? publicVideosForBanner?.videos?.[0]
      : authVideosForBanner?.data?.[0];
    return firstVideo?.thumbnail || firstVideo?.thumbnail_webp || null;
  }, [needsBannerFallback, isGuest, publicVideosForBanner, authVideosForBanner]);

  // Guest-only: locked content summary (long + shorts), used for the
  // PaywallDialog peek grid and for the TicketBand copy variants.
  const guestSummary = useCreatorGuestSummary(creator?.id, !!isGuest && !!creator?.id);
  const lockedPeek = useMemo(() => {
    if (!isGuest) return null;
    const useLong = guestSummary.lockedVideos.length > 0;
    const source = useLong ? guestSummary.lockedVideos : guestSummary.lockedShorts;
    if (source.length === 0) return null;
    const items: PeekVideoRef[] = source.slice(0, 4).map((v) => ({
      thumbnailSrc: v.thumbnail_webp ?? v.thumbnail ?? '',
      title: v.title ?? '',
    }));
    return {
      items,
      totalLockedCount: useLong ? guestSummary.totalLocked : guestSummary.totalLockedShorts,
    };
  }, [isGuest, guestSummary]);

  // PostHog: track guest channel view
  useEffect(() => {
    if (!isGuest || !creator) return;
    posthog.capture(AnalyticsEvent.FREEMIUM_CHANNEL_VIEWED, {
      handler,
      creator_name: creator.name,
      public_videos_count: creator.videos_count ?? 0,
    });
  }, [isGuest, creator, handler]);

  const totalVideos = creator?.total_videos ?? creator?.videos_count ?? 0;

  const tabs = useMemo<TabConfig[]>(() => {
    if (!creator) return [];

    const base = getCreatorRoute(handler);

    // Section counts per auth state (guest reads the public payload).
    const builtinCounts: Record<string, number | undefined> = isGuest
      ? {
          videos: creator.total_videos ?? creator.videos_count,
          shorts: creator.total_shorts,
          education: creator.course_count,
          series: creator.series_count,
          posts: creator.posts_count,
        }
      : {
          videos: creator.videos_count,
          shorts: creator.short_videos_count,
          education: creator.course_count,
          series: creator.series_count,
          posts: creator.posts_count,
        };

    // Contract merge: server row order verbatim (creator's chosen mix of
    // default markers and customs), missing defaults prepended in canonical
    // order — a never-reordered channel renders exactly the legacy layout.
    // Hide-if-empty and guest gating apply AFTER the merge, per row; guests
    // only see custom tabs with content (don't sell what doesn't exist).
    const rows: TabConfig[] = [];
    for (const row of mergeCreatorTabs(customTabs)) {
      if (row.type === 'manual') {
        if (!isGuest || row.videos_count > 0) rows.push(manualRowToTab(row, base));
        continue;
      }

      const count = builtinCounts[row.type];
      if (!systemRowVisible(count, isGuest)) continue;
      rows.push({
        key: row.type,
        label: SYSTEM_TAB_LABELS[row.type],
        ...(count !== undefined ? { count } : {}),
      });
    }

    return [{ key: 'home', label: 'Home' }, ...rows];
  }, [creator, isGuest, customTabs, handler]);

  const stats = useMemo(() => {
    if (!creator) return [];

    if (isGuest) {
      const shortsCount = creator.total_shorts ?? 0;
      return [
        ...(totalVideos > 0
          ? [{ key: 'videos', label: 'Videos', value: totalVideos, icon: <VideoIcon /> }]
          : []),
        ...(shortsCount > 0
          ? [{ key: 'shorts', label: 'Shorts', value: shortsCount, icon: <ShortsIcon /> }]
          : []),
      ];
    }

    return [
      {
        key: 'videos',
        label: 'Videos',
        value: creator.total_videos ?? creator.videos_count ?? 0,
        icon: <VideoIcon />,
      },
      {
        key: 'shorts',
        label: 'Shorts',
        value: creator.total_shorts ?? creator.short_videos_count ?? 0,
        icon: <ShortsIcon />,
      },
    ].filter((s) => Number(s.value) > 0);
  }, [creator, isGuest, totalVideos]);

  const socialLinks = useMemo(() => {
    if (!creator) return [];
    return [
      { key: 'x', url: buildSocialUrl('x', creator.x), icon: <XIcon />, label: 'X' },
      {
        key: 'tiktok',
        url: buildSocialUrl('tiktok', creator.tiktok),
        icon: <TikTokIcon />,
        label: 'TikTok',
      },
      {
        key: 'instagram',
        url: buildSocialUrl('instagram', creator.instagram),
        icon: <InstagramIcon />,
        label: 'Instagram',
      },
      {
        key: 'facebook',
        url: buildSocialUrl('facebook', creator.facebook),
        icon: <FacebookIcon />,
        label: 'Facebook',
      },
      {
        key: 'youtube',
        url: buildSocialUrl('youtube', creator.youtube),
        icon: <YouTubeIcon />,
        label: 'YouTube',
      },
    ]
      .filter((s) => s.url !== null)
      .map((s) => ({ ...s, url: s.url! }));
  }, [creator]);

  if (creatorLoading || !creator) {
    return <CreatorProfileSkeleton />;
  }

  if (creatorError) {
    return (
      <div className="min-h-dvh bg-black flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold leading-tight tracking-tight text-white m-0">
            Failed to load creator
          </h1>
          <Button
            onClick={() => refetchCreator()}
            className="px-6 py-2.5 bg-red-primary text-white text-sm font-semibold rounded hover:bg-red-hover hover:scale-105 transition-all"
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-dvh bg-black text-white">
      <CreatorHeader
        creator={creator}
        isOwner={isOwner}
        isGuest={isGuest}
        stats={stats}
        socialLinks={socialLinks}
        {...(fallbackBanner !== null ? { fallbackBanner } : {})}
      />

      <CreatorTabs
        handler={handler}
        tabs={tabs}
        isGuest={isGuest}
        creatorName={creator.name}
        {...(creator.dp !== undefined ? { creatorAvatarSrc: creator.dp } : {})}
        {...(lockedPeek !== null ? { peekVideos: lockedPeek.items } : {})}
        {...(lockedPeek !== null ? { totalLockedCount: lockedPeek.totalLockedCount } : {})}
      />

      {children}
    </div>
  );
}
